import EmailIcon from "@mui/icons-material/Email";
import { Alert, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import Name from "./components/Name";
import ParkingPermits from "./components/ParkingPermits";
import Vehicles from "./components/Vehicles";
import { Vehicle } from "./types";
import {
  useGetUserDetailsQuery,
  useGetUserParkingPermitsQuery,
  useUpdateUserDetailsMutation,
  useUpdateUserParkingPermitMutation,
} from "./userDetailsApi";
import PaymentCard from "@features/userDetails/components/PaymentCard.tsx";

export default function UserDetails() {
  const { t } = useTranslation();

  const { data: userDetails, error: fetchError } =
    useGetUserDetailsQuery(undefined);

  const { data: permits, error: permitsError } =
    useGetUserParkingPermitsQuery(undefined);

  const [updateUserDetails, { error: updateError }] =
    useUpdateUserDetailsMutation();

  const [updateUserParkingPermit, { error: updatePermitError }] =
    useUpdateUserParkingPermitMutation();

  const onPermitChange = (permitId: string, vehicle: string) => {
    void updateUserParkingPermit({
      id: permitId,
      vehicle_id: vehicle,
    });
  };

  const onNameSave = (name: { firstName: string; lastName: string }) => {
    if (userDetails) {
      void updateUserDetails({
        ...userDetails,
        first_name: name.firstName,
        last_name: name.lastName,
      });
    }
  };

  const onVehicleChange = (vehicles: Vehicle[]) => {
    if (userDetails) {
      void updateUserDetails({
        ...userDetails,
        vehicles,
      });
    }
  };

  return (
    <Stack spacing={3} padding={2}>
      <Typography variant="h4">{t("userDetailsPage.title")}</Typography>
      {(fetchError || permitsError || updatePermitError) && (
        <Alert data-testid="user-details-alert" severity="error">
          {t("unknownError")}
        </Alert>
      )}
      <Stack
        padding={2}
        spacing={2}
        border={1}
        borderColor="divider"
        borderRadius={4}
        sx={{ backgroundColor: "white" }}
      >
        {userDetails && (
          <>
            <Name
              key={JSON.stringify(userDetails)}
              firstName={userDetails.first_name}
              lastName={userDetails.last_name}
              onSave={onNameSave}
            />
            <Stack direction="row" spacing={2}>
              <EmailIcon titleAccess={t("userDetailsForm.email")} />
              <Typography variant="body1" data-testid="user-email">
                {userDetails.email}
              </Typography>
            </Stack>
          </>
        )}
      </Stack>
      {permits && userDetails && (
        <ParkingPermits
          permits={permits}
          userDetails={userDetails}
          onChange={onPermitChange}
        />
      )}
      {userDetails && (
        <Vehicles
          vehicles={userDetails.vehicles}
          onChange={onVehicleChange}
          updateError={updateError}
        />
      )}
      {userDetails?.paymentCard && (
        <PaymentCard paymentCard={userDetails.paymentCard} />
      )}
    </Stack>
  );
}
